import React, { useMemo } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format, parseISO, formatDistanceToNow, differenceInHours } from 'date-fns';
import { ProcessedNumericFact } from '../../hooks/useUserFactsDashboard';
import { cn } from '../../utils/cn';
import { TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';
import { getMetricScale, getMetricConfig } from '../../utils/wellbeingDataProcessor';

interface NumericFactChartProps {
  fact: ProcessedNumericFact;
  compact?: boolean;
  className?: string;
  showHeader?: boolean;
}

// Color palette matching the brand
const CHART_COLORS = {
  primary: '#233E59',
  accent: '#D9B059',
  teal: '#60746F',
  gradient: {
    start: 'rgba(35, 62, 89, 0.3)',
    end: 'rgba(35, 62, 89, 0.05)',
  }
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  
  return (
    <div className="bg-white border border-rm-neutral-600/20 rounded-lg shadow-card p-3 min-w-[140px]">
      <p className="text-xs text-rm-neutral-600 mb-1">
        {format(parseISO(data.date), 'MMM d, yyyy')}
      </p>
      <p className="text-lg font-semibold text-rm-primary-700">
        {data.value}
        {data.rawValue !== String(data.value) && (
          <span className="text-xs text-rm-neutral-600 ml-1">({data.rawValue})</span>
        )}
      </p>
    </div>
  );
};

const TrendLabel: React.FC<{ fact: ProcessedNumericFact }> = ({ fact }) => {
  const { trend, trendLabel } = fact;
  const metricConfig = getMetricConfig(fact.key);
  const invertedScale = metricConfig?.invertedScale ?? false;

  // Determine sentiment color based on whether the direction is good or bad
  const isPositiveDirection = invertedScale
    ? trend === 'down'
    : trend === 'up';
  const isNeutral = trend === 'stable';

  const colorClass = isNeutral
    ? 'text-rm-neutral-600 bg-rm-bg-50'
    : isPositiveDirection
      ? 'text-green-700 bg-green-50'
      : 'text-red-700 bg-red-50';

  const Icon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  return (
    <div className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
      colorClass
    )}>
      <Icon className="w-3 h-3" />
      <span>{trendLabel}</span>
    </div>
  );
};

const formatTimeAgo = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();
  const hours = differenceInHours(now, date);
  
  // Less than 24 hours
  if (hours < 24) {
    return 'Just now';
  }
  
  // Use the same format as StringFactsList
  return formatDistanceToNow(date, { addSuffix: true });
};

export const NumericFactChart: React.FC<NumericFactChartProps> = ({
  fact,
  compact = false,
  className,
  showHeader = true
}) => {
  const chartData = useMemo(() => {
    if (fact.history.length === 0) {
      // If no history, show current value as single point
      return [{
        date: new Date().toISOString(),
        value: fact.currentValue,
        rawValue: String(fact.currentValue)
      }];
    }
    return fact.history;
  }, [fact]);

  // Fixed Y-axis domain for wellbeing metrics (1-10 scale)
  const yDomain: [number, number] = [1, 10];

  // Get metric scale labels for wellbeing metrics
  const metricScale = useMemo(() => {
    return getMetricScale(fact.key);
  }, [fact.key]);

  const hasData = fact.history.length > 0;

  const lastUpdated = useMemo(() => {
    if (fact.history.length === 0) return null;
    const mostRecent = fact.history[fact.history.length - 1];
    return mostRecent.date;
  }, [fact.history]);

  if (compact) {
    return (
      <div className={cn(
        'bg-white border border-rm-neutral-600/20 rounded-card p-4 shadow-soft hover:shadow-card transition-shadow',
        className
      )}>
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-rm-neutral-600 truncate">
                {fact.displayName}
              </h3>
              {lastUpdated && (
                <div className="flex items-center gap-1 text-xs text-rm-neutral-600 shrink-0">
                  <Clock className="w-3 h-3" />
                  <span>{formatTimeAgo(lastUpdated)}</span>
                </div>
              )}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-rm-primary-700">
                {fact.currentValue}
              </span>
              {fact.history.length >= 2 && (
                <>
                  <span className="text-sm text-rm-neutral-600" title="Period average">
                    avg {fact.averageValue}
                  </span>
                  <TrendLabel fact={fact} />
                </>
              )}
            </div>
          </div>
        </div>
        
        {hasData ? (
          <div className="h-16 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <defs>
                  <linearGradient id={`gradient-${fact.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  fill={`url(#gradient-${fact.key})`}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-16 flex items-center justify-center">
            <span className="text-xs text-rm-neutral-600">No history data</span>
          </div>
        )}
      </div>
    );
  }

  // Full chart view
  return (
    <div className={cn(
      'bg-white border border-rm-neutral-600/20 rounded-card p-5 shadow-soft',
      className
    )}>
      {showHeader !== false && (
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-rm-primary-700">
              {fact.displayName}
            </h3>
            <div className="flex items-baseline gap-2 mt-1 flex-wrap">
              <span className="text-3xl font-bold text-rm-ink-900">
                {fact.currentValue}
              </span>
              {fact.history.length >= 2 && (
                <>
                  <span className="text-base text-rm-neutral-600" title="Period average">
                    avg {fact.averageValue}
                  </span>
                  <TrendLabel fact={fact} />
                </>
              )}
            </div>
          </div>
          <div className="text-right text-xs text-rm-neutral-600">
            <div>{fact.history.length} entries</div>
          </div>
        </div>
      )}

      {hasData ? (
        <div>
          {/* Top Label */}
          <div className="text-xs text-rm-neutral-600 text-left mb-1">
            {metricScale.highLabel}
          </div>
          {/* Chart */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id={`gradient-full-${fact.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => format(parseISO(date), 'MMM d')}
                  stroke="#91908E"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  domain={yDomain}
                  ticks={[1, 5, 10]}
                  stroke="#91908E"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  fill={`url(#gradient-full-${fact.key})`}
                  dot={{ fill: CHART_COLORS.primary, strokeWidth: 0, r: 3 }}
                  activeDot={{ fill: CHART_COLORS.accent, strokeWidth: 0, r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* Bottom Label */}
          <div className="text-xs text-rm-neutral-600 text-left mt-1">
            {metricScale.lowLabel}
          </div>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center border border-dashed border-rm-neutral-600/30 rounded-lg">
          <div className="text-center text-rm-neutral-600">
            <p className="text-sm">No historical data available</p>
            <p className="text-xs mt-1">Changes will be tracked over time</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NumericFactChart;
